import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from './chatMessageStyles';
import { formatDate, localizeDatesInText } from './utils/formatDate';

/**
 * HeyBro Insurance — the "digital magazine" dossier renderer.
 *
 * Boss feedback 2026-08-26: ONE long scroll is overwhelming — nobody scrolls
 * that far, and everything read as walls of text. The view is therefore built
 * as CHAPTERS: an overview page (hero, key facts, cockpit, recommendation —
 * the part that worked) plus six themed chapters behind a persistent
 * navigation (sidebar on desktop, chip bar on mobile) with prev/next paging.
 * A chapter with several dossier sections shows them as sub-tabs, so no page
 * ever becomes a long scroll again. Contacts are rendered as person cards
 * (avatar, priority, verification, contact actions), not as text.
 *
 * Purely presentational over the semi-dynamic dossier JSONB; unknown section
 * types fall back to the markdown renderer.
 *
 * LANGUAGE: dossiers are researched and stored in German (shared library) and
 * translated for viewing by the backend, so the `report` handed in here is
 * already in the UI language — except while `translating` is true, when the
 * canonical text is shown with a banner. Enum values (`bestaetigt`, `hoch`)
 * deliberately stay canonical in the data because they are styling keys; they
 * get their display label here.
 */

const ACCENT = '#19d0ef';
const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

// Verification pills — the visual backbone of the boss's quality rules.
// The VALUES stay canonical in the data (the backend's on-view translation
// deliberately preserves them, they are styling keys); only their display
// label is translated, via `labelKey`.
const VERIFICATION_STYLES = {
  bestaetigt:        { labelKey: 'heybro_v_bestaetigt',        color: '#3ddc97', bg: 'rgba(61,220,151,0.14)' },
  wahrscheinlich:    { labelKey: 'heybro_v_wahrscheinlich',    color: '#f0c24b', bg: 'rgba(240,194,75,0.14)' },
  historisch:        { labelKey: 'heybro_v_historisch',        color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.08)' },
  nicht_verifiziert: { labelKey: 'heybro_v_nicht_verifiziert', color: '#e06b6b', bg: 'rgba(224,107,107,0.12)' },
};

const ATTRACTIVENESS_STYLES = {
  hoch:    { color: '#3ddc97', bg: 'rgba(61,220,151,0.14)' },
  mittel:  { color: '#f0c24b', bg: 'rgba(240,194,75,0.14)' },
  niedrig: { color: '#e06b6b', bg: 'rgba(224,107,107,0.12)' },
};

/** Display label for a canonical level enum ('hoch' | 'mittel' | 'niedrig'). */
const levelLabel = (t, level) => t(`heybro_lvl_${level}`, { defaultValue: level });

// The chapter model: overview + six themed chapters over the section ids the
// pipeline produces. A chapter only appears when at least one of its sections
// exists, so the view degrades gracefully with partial dossiers.
const CHAPTERS = [
  { id: 'overview',    labelKey: 'heybro_ch_overview',    icon: DashboardOutlinedIcon, sectionIds: [] },
  { id: 'people',      labelKey: 'heybro_ch_people',      icon: PeopleAltOutlinedIcon, sectionIds: ['contacts', 'approach', 'buying_center'] },
  { id: 'opportunity', labelKey: 'heybro_ch_opportunity', icon: InsightsOutlinedIcon,  sectionIds: ['tarif_themen', 'hypotheses'] },
  { id: 'playbook',    labelKey: 'heybro_ch_playbook',    icon: ForumOutlinedIcon,     sectionIds: ['outreach', 'mitbestimmung'] },
  { id: 'company',     labelKey: 'heybro_ch_company',     icon: ApartmentOutlinedIcon, sectionIds: ['profile', 'benefits', 'competition'] },
  { id: 'plan',        labelKey: 'heybro_ch_plan',        icon: ChecklistOutlinedIcon, sectionIds: ['risks', 'action_plan'] },
  { id: 'sources',     labelKey: 'heybro_sources',        icon: LinkOutlinedIcon,      sectionIds: [] },
];

// Markdown typography for dossier bodies (derived from the ARE berichtSx).
const proseSx = {
  color: 'text.primary', fontSize: 13.5, lineHeight: 1.7, maxWidth: 860,
  '& h1': { fontSize: 17, fontWeight: 700, mt: 0, mb: 1 },
  '& h2': { fontSize: 15, fontWeight: 700, mt: 2, mb: 0.75, color: 'primary.main' },
  '& h3': { fontSize: 14, fontWeight: 700, mt: 1.5, mb: 0.5 },
  '& p': { my: 0.75 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& li': { mb: 0.25 },
  '& strong': { fontWeight: 700 },
  '& a': { color: 'primary.main', overflowWrap: 'anywhere' },
  // No horizontal scrollbars anywhere in the dossier (Robert, 2026-08-26):
  // markdown tables get a fixed layout and wrap inside their cells instead.
  '& table': { borderCollapse: 'collapse', my: 1, width: '100%', fontSize: 12.5, tableLayout: 'fixed' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.75, textAlign: 'left', overflowWrap: 'anywhere' },
  '& th': { bgcolor: 'action.hover', fontWeight: 700 },
  '& pre': { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  '& code': { overflowWrap: 'anywhere' },
  '& hr': { display: 'none' },
  '& > *:first-of-type': { mt: 0 },
  // Shape of the inline verification pills decorateMarkdown injects; the
  // status colours ride along as inline styles.
  '& .hb-vpill': {
    display: 'inline-block', padding: '0 7px', borderRadius: '999px',
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.3px',
    lineHeight: 1.7, whiteSpace: 'nowrap', verticalAlign: 'baseline',
  },
};

const panelSx = {
  border: '1px solid', borderColor: 'divider', borderRadius: '12px',
  bgcolor: 'rgba(255,255,255,0.03)', p: { xs: 1.75, md: 2.25 },
};

const microLabelSx = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: 'text.secondary', mb: 0.25,
};

// The research content carries inline quality markers the boss's prompts
// require — "[bestaetigt]", "[nicht_verifiziert]", "(Abruf 2026-08-26)".
// Raw, they read like a bug (boss feedback 2026-08-26): every displayed text
// therefore runs through this decoration — markers become the same pills the
// contact cards use, ISO dates become the user's locale format.
const TOKEN_RE = /\[(bestaetigt|bestätigt|wahrscheinlich|historisch|nicht[_\s-]?verifiziert)\]/gi;

const canonicalStatus = (raw) => {
  const v = raw.toLowerCase().replace(/ä/g, 'ae').replace(/[\s-]+/g, '_');
  return v.startsWith('nicht') ? 'nicht_verifiziert' : v;
};

/** Markdown bodies: decorate the STRING before parsing (rehypeRaw renders the
 * injected pill spans); shape comes from the .hb-vpill rule in proseSx. */
const decorateMarkdown = (text, language, t) =>
  localizeDatesInText(text || '', language).replace(TOKEN_RE, (m, raw) => {
    const s = VERIFICATION_STYLES[canonicalStatus(raw)];
    return s
      ? `<span class="hb-vpill" style="color:${s.color};background:${s.bg};border:1px solid ${s.color}44">${t(s.labelKey)}</span>`
      : m;
  });

const Md = ({ children }) => {
  const { t, i18n } = useTranslation();
  const findSourceUrl = useContext(SourceLinksContext);
  let processed = decorateMarkdown(children, i18n.language, t);
  // Same inline source linking as AnnotatedText, injected as raw anchors
  // (rehypeRaw renders them; proseSx styles them like every other link).
  processed = processed.replace(new RegExp(SRC_MENTION_RE.source, 'gi'), (whole, prefix, segment) => {
    const linked = segment.split(/(\s+und\s+|\s+sowie\s+)/).map((piece) => {
      const url = /^\s*(und|sowie)\s*$/.test(piece) ? null : findSourceUrl(piece);
      return url ? `<a href="${url}" target="_blank" rel="noreferrer">${piece}</a>` : piece;
    }).join('');
    return `${prefix}${linked}`;
  });
  return (
    <Box sx={proseSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {processed}
      </ReactMarkdown>
    </Box>
  );
};

// ── Inline source linking ───────────────────────────────────────────────────
// Cockpit/signal texts name their sources inline ("Quelle: Konzerngeschäfts-
// bericht Saarstahl 2025 …") while the dossier's sources list carries the
// actual URLs. DossierView provides a matcher over that list via context;
// every "Quelle:"-mention that confidently matches a listed source becomes a
// link to it. No confident match → plain text (never a wrong link).

const SourceLinksContext = createContext(() => null);

const _tokenize = (s) => new Set(
  (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9ß]+/)
    .filter((w) => w.length >= 4 || /^\d{4}$/.test(w))
);

const _tokenMatches = (w, tokens) => {
  if (tokens.has(w)) return true;
  // Compounds: "konzerngeschaftsbericht" should still hit "geschaftsbericht".
  if (w.length >= 6) {
    for (const tk of tokens) {
      if (tk.length >= 6 && (tk.includes(w) || w.includes(tk))) return true;
    }
  }
  return false;
};

/** Build the mention → URL matcher over the dossier's sources list. */
const buildSourceMatcher = (sources) => {
  const index = (sources || [])
    .filter((s) => s.url)
    .map((s) => ({ url: s.url, tokens: _tokenize(`${s.title || ''} ${s.publisher || ''}`) }));
  return (mention) => {
    const words = [..._tokenize(mention)];
    if (!words.length || !index.length) return null;
    let best = null;
    let bestScore = 0;
    for (const s of index) {
      let score = 0;
      for (const w of words) if (_tokenMatches(w, s.tokens)) score += 1;
      if (score > bestScore) { bestScore = score; best = s; }
    }
    // Confident = at least half the mention's meaningful words (min 2) matched.
    return bestScore >= Math.max(2, Math.ceil(words.length * 0.5)) ? best.url : null;
  };
};

const SRC_MENTION_RE = /(Quellen?\s*:\s*)([^;\]\n]+)/gi;

const srcLinkStyle = {
  color: ACCENT, textDecoration: 'underline dotted', textUnderlineOffset: '2px',
};

/** Wrap matched "Quelle: …" mentions inside a plain string in links. */
const renderWithSourceLinks = (str, findSourceUrl, keyBase) => {
  const out = [];
  const re = new RegExp(SRC_MENTION_RE.source, 'gi');
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(str))) {
    if (m.index > last) out.push(<span key={`${keyBase}-t${k++}`}>{str.slice(last, m.index)}</span>);
    out.push(<span key={`${keyBase}-l${k++}`}>{m[1]}</span>);
    // A mention may name several sources ("… und …"): link each part on its own.
    const pieces = m[2].split(/(\s+und\s+|\s+sowie\s+)/);
    for (const piece of pieces) {
      const url = /^\s*(und|sowie)\s*$/.test(piece) ? null : findSourceUrl(piece);
      out.push(url
        ? <a key={`${keyBase}-a${k++}`} href={url} target="_blank" rel="noreferrer" style={srcLinkStyle}>{piece}</a>
        : <span key={`${keyBase}-p${k++}`}>{piece}</span>);
    }
    last = re.lastIndex;
  }
  out.push(<span key={`${keyBase}-t${k++}`}>{str.slice(last)}</span>);
  return out;
};

/** Plain-text contexts (Typography children): same decoration as React nodes. */
function AnnotatedText({ text }) {
  const { i18n } = useTranslation();
  const findSourceUrl = useContext(SourceLinksContext);
  if (typeof text !== 'string' || !text) return text ?? null;
  const parts = [];
  const re = new RegExp(TOKEN_RE.source, 'gi');
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(localizeDatesInText(text.slice(last, m.index), i18n.language));
    parts.push({ pill: canonicalStatus(m[1]) });
    last = re.lastIndex;
  }
  parts.push(localizeDatesInText(text.slice(last), i18n.language));
  return (
    <>
      {parts.map((p, i) => (typeof p === 'string'
        ? <span key={i}>{renderWithSourceLinks(p, findSourceUrl, i)}</span>
        : <VerificationPill key={i} status={p.pill} />))}
    </>
  );
}

function VerificationPill({ status }) {
  const { t } = useTranslation();
  const s = VERIFICATION_STYLES[status];
  if (!s) return null;
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.1, borderRadius: '999px', fontSize: 10.5,
      fontWeight: 700, letterSpacing: '0.3px', color: s.color, bgcolor: s.bg,
      border: `1px solid ${s.color}44`, whiteSpace: 'nowrap',
    }}>
      {t(s.labelKey)}
    </Box>
  );
}

function CopyButton({ text, t }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { /* clipboard blocked — nothing to do */ }
  }, [text]);
  return (
    <Button size="small" onClick={copy}
      startIcon={copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: copied ? '#3ddc97' : 'text.secondary',
        '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}>
      {copied ? t('heybro_copied') : t('heybro_copy')}
    </Button>
  );
}

// ─── Progressive-disclosure building blocks ────────────────────────────────
// Boss feedback 2026-08-26: the first look of every chapter must be light and
// visual — the full text opens on demand (modal / accordion / "Mehr").

/** Shared detail modal — the "click for the whole story" surface. */
function DetailModal({ open, onClose, title, subtitle, children }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="div" sx={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.35 }}>
          <AnnotatedText text={title} />
        </Typography>
        {subtitle && (
          <Typography component="div" sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
            <AnnotatedText text={subtitle} />
          </Typography>
        )}
        <IconButton onClick={onClose} size="small"
          sx={{ position: 'absolute', right: 10, top: 10, color: 'text.secondary' }} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 2.5 }}>{children}</DialogContent>
    </Dialog>
  );
}

const stripMd = (md) => (md || '')
  .replace(/[#*_`>]/g, '')
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/\s+/g, ' ')
  .trim();

/** Section intro: two quiet lines, the rest behind "Mehr anzeigen". */
function ClampedIntro({ text }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const plain = stripMd(text);
  const needsToggle = plain.length > 180;
  return (
    <Box sx={{ mb: 1.75 }}>
      {open
        ? <Md>{text}</Md>
        : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6, maxWidth: 860,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            <AnnotatedText text={plain} />
          </Typography>
        )}
      {needsToggle && (
        <ButtonBase onClick={() => setOpen((o) => !o)}
          sx={{ mt: 0.5, gap: 0.5, borderRadius: '6px', px: 0.5, fontSize: 11.5, fontWeight: 600,
            color: 'primary.main' }}>
          <ExpandMoreIcon sx={{ fontSize: 15, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          {open ? t('heybro_less') : t('heybro_more')}
        </ButtonBase>
      )}
    </Box>
  );
}

/** Split a markdown body at its headings (### … or bold-only lines) so a text
 * section can render as accordions instead of a wall of prose. */
const splitMdSections = (md) => {
  const sections = [];
  let current = { title: null, body: [] };
  for (const line of (md || '').split('\n')) {
    const h = line.match(/^#{2,4}\s+(.+?)\s*$/) || line.match(/^\*\*([^*]+?)\*\*:?\s*$/);
    if (h) {
      if (current.title || current.body.join('').trim()) sections.push(current);
      current = { title: h[1], body: [] };
    } else {
      current.body.push(line);
    }
  }
  if (current.title || current.body.join('').trim()) sections.push(current);
  return sections;
};

/** A `text`-type dossier section: preamble clamped, every sub-heading a
 * closed accordion row — the reader opens what interests them. */
function TextSection({ content }) {
  const [open, setOpen] = useState({});
  const text = content.body_md || '';
  const parts = splitMdSections(text);
  const titled = parts.filter((p) => p.title);
  if (titled.length < 2) return <Md>{text}</Md>;   // little structure → plain
  const preamble = parts.find((p) => !p.title);
  return (
    <Box>
      {preamble && <ClampedIntro text={preamble.body.join('\n')} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {titled.map((p, i) => (
          <Box key={i} sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
            <ButtonBase onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
              sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 1.75, py: 1.4,
                textAlign: 'left', '&:hover': { bgcolor: 'action.hover' } }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                {p.title}
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0,
                transform: open[i] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </ButtonBase>
            <Collapse in={!!open[i]}>
              <Box sx={{ px: 1.75, pb: 1.75 }}>
                <Md>{p.body.join('\n')}</Md>
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Contacts: person cards instead of text ─────────────────────────────────

const AVATAR_COLORS = ['#19d0ef', '#a78bfa', '#3ddc97', '#f0c24b', '#f78fb3', '#7ec8ff'];

// Initials come ONLY from a real person name (never from a function title —
// "Leitung (Corporate/Group)" once rendered as "L(", boss feedback
// 2026-08-26). Title tokens like "Dr." are skipped.
const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/)
    .filter((p) => /^[A-Za-zÀ-ÿ]/.test(p) && !/\.$/.test(p));
  if (!parts.length) return '';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const avatarColor = (seed) => {
  let h = 0;
  for (const ch of seed || '?') h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

/**
 * One contact — a STRICT, identical structure for every card, whatever the
 * data holds (boss feedback 2026-08-26: cards must never re-flow into
 * different layouts):
 *
 *   [avatar]  badges row (priority + verification)   ← always the first line
 *             title (name, or role for function-only contacts)
 *             subtitle (role · org · location)
 *   ─────────────────────────────────────────────
 *   Details ⌄                        [phone][mail][in]   ← fixed footer
 *
 * The avatar is top-anchored; named people get their initials in colour,
 * function-only contacts a neutral person icon. Everything long (opener,
 * influence, contact channel, source) lives behind the Details toggle.
 */
function PersonCard({ row, t, featured = false }) {
  const [open, setOpen] = useState(false);
  const isNamed = !!initialsOf(row.name);
  const color = avatarColor(row.name || row.role);
  const title = isNamed ? row.name : (row.role || '—');
  const subtitle = [isNamed ? row.role : null, row.org, row.location].filter(Boolean).join(' · ');
  const details = [
    row.opener && { label: t('heybro_f_opener'), text: `„${row.opener}“`, italic: true },
    (row.influence || row.relevance) && {
      label: t('heybro_f_influence'),
      text: [row.influence, row.relevance].filter(Boolean).join(' — '),
    },
    row.channel && { label: t('heybro_f_channel'), text: row.channel },
    (row.source || row.as_of) && {
      label: t('heybro_f_source_date'),
      text: [row.source, row.as_of].filter(Boolean).join(' · '),
    },
  ].filter(Boolean);
  const size = featured ? 52 : 42;
  const actionSx = { color: 'primary.main', bgcolor: hexA(ACCENT, 0.1), '&:hover': { bgcolor: hexA(ACCENT, 0.2) } };
  return (
    <Box sx={{ ...panelSx, p: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column',
      ...(featured && { borderColor: hexA(ACCENT, 0.45), background: `linear-gradient(135deg, ${hexA(ACCENT, 0.08)}, transparent 65%)` }) }}>
      {/* ── Header: avatar + fixed three-line stack ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.75, pb: 1.25 }}>
        <Box sx={{ width: size, height: size, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: featured ? 18 : 15, fontWeight: 800,
          color: isNamed ? color : 'text.secondary',
          bgcolor: isNamed ? `${color}22` : 'action.hover',
          border: `2px solid ${isNamed ? `${color}66` : 'transparent'}` }}>
          {isNamed ? initialsOf(row.name) : <PersonOutlineIcon sx={{ fontSize: featured ? 26 : 22 }} />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Line 1 — badges, always on their own row so every card aligns */}
          {(row.priority || VERIFICATION_STYLES[row.verification]) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              {row.priority && (
                <Box component="span" sx={{ px: 0.9, py: 0.1, borderRadius: '7px', fontSize: 11, fontWeight: 800,
                  color: 'primary.main', bgcolor: hexA(ACCENT, 0.14), flexShrink: 0 }}>
                  {row.priority}
                </Box>
              )}
              <VerificationPill status={row.verification} />
            </Box>
          )}
          {/* Line 2 — title */}
          <Typography sx={{ fontSize: featured ? 16 : 14, fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {/* Line 3 — subtitle */}
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {/* ── Footer: Details toggle left, contact actions right — fixed place ── */}
      <Box sx={{ mt: 'auto', px: 1.75, py: 0.75, display: 'flex', alignItems: 'center', gap: 1,
        borderTop: '1px solid', borderColor: 'divider' }}>
        {details.length > 0 ? (
          <ButtonBase onClick={() => setOpen((o) => !o)}
            sx={{ gap: 0.5, borderRadius: '6px', px: 0.5, fontSize: 11.5, fontWeight: 600,
              color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            <ExpandMoreIcon sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            {t('heybro_details')}
          </ButtonBase>
        ) : <span />}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          {row.phone && (
            <InfoTooltip title={row.phone}>
              <IconButton size="small" href={`tel:${row.phone.replace(/\s+/g, '')}`} sx={actionSx}>
                <PhoneIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InfoTooltip>
          )}
          {row.email && (
            <InfoTooltip title={row.email}>
              <IconButton size="small" href={`mailto:${row.email}`} sx={actionSx}>
                <MailOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InfoTooltip>
          )}
          {row.linkedin && (
            <InfoTooltip title="LinkedIn">
              <IconButton size="small" href={row.linkedin} target="_blank" rel="noreferrer" sx={actionSx}>
                <LinkedInIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InfoTooltip>
          )}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.75, py: 1.5, borderTop: '1px solid', borderColor: 'divider',
          display: 'grid', gridTemplateColumns: { xs: '1fr', md: featured ? '1fr 1fr' : '1fr' }, gap: 1 }}>
          {details.map((d) => (
            <Box key={d.label}>
              <Typography sx={microLabelSx}>{d.label}</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, overflowWrap: 'anywhere',
                ...(d.italic && { fontStyle: 'italic' }) }}>
                <AnnotatedText text={d.text} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function ContactsBlock({ content, t }) {
  const rows = content.rows || [];
  const tierOf = (r) => ((r.priority || '')[0] || 'C').toUpperCase();
  const aRows = rows.filter(r => tierOf(r) === 'A');
  const bRows = rows.filter(r => tierOf(r) === 'B');
  const rest = rows.filter(r => !'AB'.includes(tierOf(r)));
  const [first, ...moreA] = aRows;
  const group = (label, items, featuredFirst = false) => items.length > 0 && (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography sx={{ ...microLabelSx, color: 'primary.main', mb: 1 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {featuredFirst && first && <PersonCard row={first} t={t} featured />}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          {(featuredFirst ? moreA : items).map((r, i) => <PersonCard key={i} row={r} t={t} />)}
        </Box>
      </Box>
    </Box>
  );
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      {group(t('heybro_prio_a'), aRows, true)}
      {group(t('heybro_prio_b'), bRows)}
      {group(t('heybro_prio_other'), rest)}
    </Box>
  );
}

// ─── Other section renderers ────────────────────────────────────────────────

function ApproachSequence({ content }) {
  const steps = content.steps || [];
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 720 }}>
        {steps.map((s, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: i < steps.length - 1 ? 2 : 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'primary.main',
                bgcolor: hexA(ACCENT, 0.14), border: `1px solid ${hexA(ACCENT, 0.4)}` }}>
                {s.order ?? i + 1}
              </Box>
              {i < steps.length - 1 && (
                <Box sx={{ flex: 1, width: '2px', bgcolor: hexA(ACCENT, 0.2), mt: 0.5 }} />
              )}
            </Box>
            <Box sx={{ pt: 0.25, pb: 0.5, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}><AnnotatedText text={s.who} /></Typography>
                {s.when && (
                  <Chip size="small" label={s.when} sx={{ height: 20, fontSize: 10.5, bgcolor: 'action.hover', color: 'text.secondary' }} />
                )}
              </Box>
              {s.why && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}><AnnotatedText text={s.why} /></Typography>}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CardsBlock({ content }) {
  // Buying-center roles as compact clickable tiles: role, name, a two-line
  // teaser — the full assessment opens in a modal.
  const { t } = useTranslation();
  const [modal, setModal] = useState(null);
  const cards = content.cards || [];
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
        {cards.map((c, i) => (
          <ButtonBase key={i} onClick={() => setModal(c)}
            sx={{ ...panelSx, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              textAlign: 'left', gap: 0.5, transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': { borderColor: hexA(ACCENT, 0.5), bgcolor: hexA(ACCENT, 0.05) } }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase',
              color: 'primary.main', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {c.title}
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}>
              {c.badge || '—'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {stripMd(c.body_md)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 'auto', pt: 0.25 }}>
              <ArrowForwardIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'primary.main' }}>{t('heybro_details')}</Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
      <DetailModal open={!!modal} onClose={() => setModal(null)}
        title={modal?.title || ''} subtitle={modal?.badge || null}>
        <Md>{modal?.body_md || ''}</Md>
      </DetailModal>
    </Box>
  );
}

function TableBlock({ content }) {
  // Events (e.g. Tarif-/Betriebsthemen) as a lean timeline: date + one-line
  // headline per row, everything else (Akteure, Positionen, Quelle) in the
  // detail modal. Never a real <table>, never a horizontal scrollbar.
  const [modal, setModal] = useState(null);
  const cols = content.columns || [];
  const rows = (content.rows || []).map((r) => (Array.isArray(r) ? r : cols.map((c) => r?.[c])));
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((r, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
            {/* Timeline rail */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 12 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', mt: 1.6, flexShrink: 0,
                bgcolor: hexA(ACCENT, 0.9), boxShadow: `0 0 8px ${hexA(ACCENT, 0.6)}` }} />
              {i < rows.length - 1 && <Box sx={{ flex: 1, width: '2px', bgcolor: hexA(ACCENT, 0.18) }} />}
            </Box>
            <ButtonBase onClick={() => setModal(r)}
              sx={{ flex: 1, minWidth: 0, mb: 1, p: 1.25, borderRadius: '10px', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'flex-start',
                border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.02)',
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': { borderColor: hexA(ACCENT, 0.5), bgcolor: hexA(ACCENT, 0.05) } }}>
              {r[0] != null && r[0] !== '' && (
                <Chip size="small" label={<AnnotatedText text={String(r[0])} />}
                  sx={{ height: 22, fontSize: 11, fontWeight: 700, color: 'primary.main',
                    bgcolor: hexA(ACCENT, 0.12), flexShrink: 0 }} />
              )}
              <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.4,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                <AnnotatedText text={String(r[1] ?? r[0] ?? '')} />
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
            </ButtonBase>
          </Box>
        ))}
      </Box>
      <DetailModal open={!!modal} onClose={() => setModal(null)}
        title={modal ? String(modal[1] ?? modal[0] ?? '') : ''}
        subtitle={modal && modal[0] != null ? String(modal[0]) : null}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {modal && cols.slice(2).map((label, j) => {
            const v = modal[j + 2];
            if (v == null || v === '') return null;
            return (
              <Box key={j}>
                <Typography sx={microLabelSx}>{label}</Typography>
                <Typography sx={{ fontSize: 13, lineHeight: 1.6, overflowWrap: 'anywhere' }}>
                  {typeof v === 'string' ? <AnnotatedText text={v} /> : v}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DetailModal>
    </Box>
  );
}

function Hypotheses({ content }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState({});
  const items = content.items || [];
  const confidenceColor = { hoch: '#3ddc97', mittel: '#f0c24b', niedrig: '#e06b6b' };
  const FIELDS = [
    ['need', t('heybro_f_need')], ['target_person', t('heybro_f_target_person')],
    ['benefit', t('heybro_f_benefit')], ['check_question', t('heybro_f_check_question')],
    ['objection', t('heybro_f_objection')], ['answer', t('heybro_f_answer')],
  ];
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((h, i) => (
          <Box key={i} sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
            <Box onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' } }}>
              <LightbulbOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0 }}><AnnotatedText text={h.signal} /></Typography>
              {h.confidence && (
                <Chip size="small" label={levelLabel(t, h.confidence)} sx={{ height: 20, fontSize: 10.5, fontWeight: 700,
                  color: confidenceColor[h.confidence] || 'text.secondary',
                  bgcolor: `${confidenceColor[h.confidence] || '#888'}22` }} />
              )}
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary',
                transform: open[i] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </Box>
            <Collapse in={!!open[i]}>
              <Box sx={{ px: 1.75, pb: 1.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                {FIELDS.map(([key, label]) => h[key] && (
                  <Box key={key}>
                    <Typography sx={microLabelSx}>{label}</Typography>
                    <Typography sx={{ fontSize: 12.5, lineHeight: 1.55 }}><AnnotatedText text={h[key]} /></Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const OUTREACH_ICONS = {
  phone: PhoneIcon,
  email_br: MailOutlineIcon,
  email_union: MailOutlineIcon,
  email_hr: MailOutlineIcon,
  linkedin: LinkedInIcon,
  discovery: HelpOutlineIcon,
  objections: ShieldOutlinedIcon,
};

function Outreach({ content, t }) {
  // Template gallery: one tile per Vorlage (icon + title + copy), the full
  // wording opens in a modal — no template body on the first look.
  const [modal, setModal] = useState(null);
  const items = content.items || [];
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
        {items.map((o, i) => {
          const Icon = OUTREACH_ICONS[o.kind] || DescriptionOutlinedIcon;
          return (
            <Box key={i} sx={{ ...panelSx, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': { borderColor: hexA(ACCENT, 0.5), bgcolor: hexA(ACCENT, 0.04) } }}>
              <ButtonBase onClick={() => setModal(o)}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                  p: 2, pb: 1.25, textAlign: 'center', width: '100%' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', bgcolor: hexA(ACCENT, 0.12), border: `1px solid ${hexA(ACCENT, 0.3)}` }}>
                  <Icon sx={{ fontSize: 21, color: 'primary.main' }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>
                  {o.title}
                </Typography>
              </ButtonBase>
              <Box sx={{ mt: 'auto', px: 1, py: 0.5, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button size="small" onClick={() => setModal(o)}
                  sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 600, color: 'primary.main' }}>
                  {t('heybro_open')}
                </Button>
                <CopyButton text={o.body_md} t={t} />
              </Box>
            </Box>
          );
        })}
      </Box>
      <DetailModal open={!!modal} onClose={() => setModal(null)} title={modal?.title || ''}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <CopyButton text={modal?.body_md} t={t} />
        </Box>
        <Md>{modal?.body_md || ''}</Md>
      </DetailModal>
    </Box>
  );
}

// The model often writes "hoch – lange Erklärung…" into impact; the chip
// shows ONLY the severity word, the explanation moves into the modal.
// `impact` is prose, so a TRANSLATED dossier carries the severity word in the
// reader's language — every variant maps back to the canonical key that drives
// the colour, and the chip then shows the localised label.
const SEVERITY_WORDS = {
  hoch: 'hoch', high: 'hoch', 'élevé': 'hoch', 'élevée': 'hoch', eleve: 'hoch',
  alto: 'hoch', alta: 'hoch',
  mittel: 'mittel', medium: 'mittel', moyen: 'mittel', moyenne: 'mittel',
  medio: 'mittel', media: 'mittel',
  niedrig: 'niedrig', low: 'niedrig', faible: 'niedrig', bajo: 'niedrig',
  baja: 'niedrig', basso: 'niedrig', bassa: 'niedrig',
};
const SEVERITY_RE = new RegExp(
  `^\\s*(${Object.keys(SEVERITY_WORDS).sort((a, b) => b.length - a.length).join('|')})\\b[\\s–—:-]*(.*)$`,
  'is');

const splitImpact = (impact) => {
  const m = String(impact || '').match(SEVERITY_RE);
  const severity = m && SEVERITY_WORDS[m[1].toLowerCase()];
  if (severity) return { severity, note: (m[2] || '').trim() || null };
  return { severity: null, note: String(impact || '').trim() || null };
};

const SEVERITY_COLORS = { hoch: '#e06b6b', mittel: '#f0c24b', niedrig: '#3ddc97' };

function RiskTable({ content }) {
  // Lean risk register: icon + one-line risk + severity chip; actors,
  // reaction, recommendation and the full impact note live in the modal.
  const { t } = useTranslation();
  const [modal, setModal] = useState(null);
  const rows = content.rows || [];
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.map((r, i) => {
          const { severity } = splitImpact(r.impact);
          const color = SEVERITY_COLORS[severity] || '#f0c24b';
          return (
            <ButtonBase key={i} onClick={() => setModal(r)}
              sx={{ ...panelSx, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25,
                justifyContent: 'flex-start', textAlign: 'left', width: '100%',
                borderLeft: `3px solid ${color}`,
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': { bgcolor: hexA(ACCENT, 0.05) } }}>
              <WarningAmberIcon sx={{ fontSize: 17, color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.4,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                <AnnotatedText text={r.risk} />
              </Typography>
              {severity && (
                <Chip size="small" label={levelLabel(t, severity)}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, color, bgcolor: `${color}22`, flexShrink: 0 }} />
              )}
              <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
            </ButtonBase>
          );
        })}
      </Box>
      <DetailModal open={!!modal} onClose={() => setModal(null)} title={modal?.risk || ''}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {modal && (() => {
            const { severity, note } = splitImpact(modal.impact);
            return (severity || note) ? (
              <Box>
                <Typography sx={microLabelSx}>{t('heybro_f_impact')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {severity && (
                    <Chip size="small" label={levelLabel(t, severity)}
                      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, mt: 0.25, flexShrink: 0,
                        color: SEVERITY_COLORS[severity], bgcolor: `${SEVERITY_COLORS[severity]}22` }} />
                  )}
                  {note && (
                    <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}><AnnotatedText text={note} /></Typography>
                  )}
                </Box>
              </Box>
            ) : null;
          })()}
          {modal && [['actors', t('heybro_f_actors')], ['reaction', t('heybro_f_reaction')],
            ['recommendation', t('heybro_f_recommendation')]]
            .map(([key, label]) => modal[key] && (
              <Box key={key}>
                <Typography sx={microLabelSx}>{label}</Typography>
                <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}><AnnotatedText text={modal[key]} /></Typography>
              </Box>
            ))}
        </Box>
      </DetailModal>
    </Box>
  );
}

function ActionPlan({ content }) {
  const { t } = useTranslation();
  return (
    <Box>
      <ClampedIntro text={content.intro} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min((content.horizons || []).length || 1, 3)}, 1fr)` }, gap: 1.25 }}>
        {(content.horizons || []).map((h, i) => (
          <Box key={i} sx={{ ...panelSx, p: 1.75 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase',
              color: 'primary.main', mb: 1 }}>
              {h.label}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              {(h.items || []).map((item, j) => (
                <Typography key={j} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>{item}</Typography>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      {(content.success_criteria?.length || content.abort_criteria?.length) ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1.25 }}>
          {content.success_criteria?.length > 0 && (
            <Box sx={{ ...panelSx, p: 1.75, borderColor: 'rgba(61,220,151,0.35)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#3ddc97', mb: 0.75 }}>
                {t('heybro_success_criteria')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {content.success_criteria.map((c, i) => (
                  <Typography key={i} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>{c}</Typography>
                ))}
              </Box>
            </Box>
          )}
          {content.abort_criteria?.length > 0 && (
            <Box sx={{ ...panelSx, p: 1.75, borderColor: 'rgba(224,107,107,0.35)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#e06b6b', mb: 0.75 }}>
                {t('heybro_abort_criteria')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {content.abort_criteria.map((c, i) => (
                  <Typography key={i} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>{c}</Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

function renderSectionContent(section, t) {
  const content = section.content || {};
  switch (section.type) {
    case 'contacts_table': return <ContactsBlock content={content} t={t} />;
    case 'approach_sequence': return <ApproachSequence content={content} />;
    case 'cards': return <CardsBlock content={content} />;
    case 'table': return <TableBlock content={content} />;
    case 'hypotheses': return <Hypotheses content={content} />;
    case 'outreach': return <Outreach content={content} t={t} />;
    case 'risk_table': return <RiskTable content={content} />;
    case 'action_plan': return <ActionPlan content={content} />;
    case 'text':
    default:
      return <TextSection content={content} />;
  }
}

// ─── The magazine ───────────────────────────────────────────────────────────

function DossierView({ report, onBack, onRefresh, onDelete, onExport, translating = false }) {
  const { t, i18n } = useTranslation();
  // Word export: the button spins while the backend renders the .docx.
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (!onExport || exporting) return;
    setExporting(true);
    try { await onExport(); } finally { setExporting(false); }
  }, [onExport, exporting]);
  const doc = report.dossier || {};
  const meta = doc.meta || {};
  const cockpit = doc.cockpit || {};
  const keyFacts = doc.key_facts || [];
  const sections = useMemo(() => doc.sections || [], [doc.sections]);
  const sources = useMemo(() => doc.sources || [], [doc.sources]);
  const rec = doc.final_recommendation || {};

  const sectionById = useMemo(() => {
    const map = {};
    sections.forEach((s) => { map[s.id] = s; });
    return map;
  }, [sections]);

  // Chapters that actually have content in this dossier.
  const chapters = useMemo(() => CHAPTERS.filter((ch) => {
    if (ch.id === 'overview') return true;
    if (ch.id === 'sources') return sources.length > 0;
    return ch.sectionIds.some((id) => sectionById[id]);
  }), [sectionById, sources.length]);

  const [chapterId, setChapterId] = useState('overview');
  // Active sub-tab per chapter: {chapterId: sectionId}
  const [subTab, setSubTab] = useState({});
  // Inline "Quelle: …" mentions link to the matching entry of the sources
  // list (provided to AnnotatedText/Md via context).
  const findSourceUrl = useMemo(() => buildSourceMatcher(sources), [sources]);

  const chapterIndex = chapters.findIndex((c) => c.id === chapterId);
  const chapter = chapters[chapterIndex] || chapters[0];

  const goTo = useCallback((id) => setChapterId(id), []);

  const attractiveness = meta.attractiveness && ATTRACTIVENESS_STYLES[meta.attractiveness];

  const trendIcon = (trend) => (
    trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 15, color: '#3ddc97' }} />
      : trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 15, color: '#e06b6b' }} />
      : trend === 'flat' ? <TrendingFlatIcon sx={{ fontSize: 15, color: 'text.secondary' }} /> : null
  );

  // The engine sometimes stuffs comparisons and half sentences into `value`
  // ("132,2 Mrd. € (2024: 145,6 Mrd. €)", "rund 108.000 Beschäftigte in
  // Deutschland") — rendered big and cyan that looked terrible (boss feedback
  // 2026-08-26). Split each fact into a short MAIN figure, an optional small
  // SUB line, and the label; the compose prompt is stricter now, this keeps
  // already-generated dossiers clean too.
  const splitFact = (raw) => {
    const value = String(raw || '').trim();
    const paren = value.match(/^(.*?)\s*\((.+?)\)\s*$/);
    if (paren && paren[1]) return { main: paren[1].trim(), sub: paren[2].trim() };
    if (value.length <= 18) return { main: value, sub: null };
    const m = value.match(
      /^((?:rund|ca\.?|über|mehr als|knapp|fast|etwa|unter|weniger als)?\s*[\d][\d.,\s]*(?:\s*(?:Mrd|Mio|Tsd)\.?)?(?:\s*(?:€|CHF|EUR|USD|%))?)\s+(.{3,})$/i
    );
    if (m && m[1].trim()) return { main: m[1].trim(), sub: m[2].trim() };
    return { main: value, sub: null };
  };

  // Drop a sub line that only repeats the label ("Beschäftigte in Deutschland"
  // under a "Beschäftigte in Deutschland (…)" label).
  const subIsRedundant = (sub, label) => {
    if (!sub || !label) return false;
    const lab = label.toLowerCase();
    const words = sub.toLowerCase().split(/[^a-zà-ÿ0-9]+/).filter((w) => w.length > 3);
    return words.length > 0 && words.every((w) => lab.includes(w));
  };

  // ── Overview: hero + key facts + cockpit + recommendation + chapter cards ──
  const renderOverview = () => (
    <>
      <Box sx={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', p: { xs: 2, md: 3 }, mb: 2,
        background: `linear-gradient(135deg, ${hexA(ACCENT, 0.16)} 0%, rgba(167,139,250,0.10) 60%, transparent 100%)`,
        border: `1px solid ${hexA(ACCENT, 0.25)}` }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.6px', textTransform: 'uppercase',
          color: 'primary.main', mb: 0.5 }}>
          bKV Company Dossier
        </Typography>
        <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800, lineHeight: 1.15 }}>
          {meta.company_name || report.company_name}
        </Typography>
        {meta.headline && (
          <Typography sx={{ fontSize: { xs: 14, md: 15.5 }, color: 'text.secondary', mt: 0.75, maxWidth: 720 }}>
            <AnnotatedText text={meta.headline} />
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {attractiveness && (
            <Chip size="small" label={`${t('heybro_attractiveness')}: ${levelLabel(t, meta.attractiveness)}`}
              sx={{ height: 24, fontSize: 11.5, fontWeight: 700, color: attractiveness.color, bgcolor: attractiveness.bg }} />
          )}
          {meta.industry && <Chip size="small" label={meta.industry} sx={{ height: 24, fontSize: 11.5, bgcolor: 'action.hover', color: 'text.primary' }} />}
          {meta.region && <Chip size="small" label={meta.region} sx={{ height: 24, fontSize: 11.5, bgcolor: 'action.hover', color: 'text.secondary' }} />}
          {meta.website && (
            <Chip size="small" clickable component="a" href={meta.website} target="_blank" rel="noreferrer"
              icon={<OpenInNewIcon sx={{ fontSize: 13 }} />} label={meta.website.replace(/^https?:\/\/(www\.)?/, '')}
              sx={{ height: 24, fontSize: 11.5, bgcolor: 'action.hover', color: 'primary.main' }} />
          )}
        </Box>
      </Box>

      {keyFacts.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(keyFacts.length, 5)}, 1fr)` }, gap: 1, mb: 2 }}>
          {keyFacts.map((f, i) => {
            // A backend-provided sub wins; otherwise parse it out of value.
            const { main, sub } = f.sub
              ? { main: String(f.value || '').trim(), sub: f.sub }
              : splitFact(f.value);
            const subLine = f.sub || (subIsRedundant(sub, f.label) ? null : sub);
            // Long figures shrink instead of wrapping — every tile keeps the
            // same three fixed slots: value / sub / label.
            const mainSize = main.length > 16 ? 13 : main.length > 11 ? 15 : 17;
            return (
              <Box key={i} sx={{ ...panelSx, p: 1.5, textAlign: 'center', minHeight: 92,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                  <Typography sx={{ fontSize: mainSize, fontWeight: 800, color: 'primary.main',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                    {main}
                  </Typography>
                  {trendIcon(f.trend)}
                </Box>
                {subLine && (
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                    {subLine}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', letterSpacing: '0.3px',
                  lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {f.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {(cockpit.key_insights || []).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'primary.main', mb: 1.25 }}>
            {t('heybro_cockpit')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
            {cockpit.key_insights.map((ins, i) => (
              <Box key={i} sx={{ ...panelSx, p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                  color: 'primary.main', bgcolor: hexA(ACCENT, 0.14) }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 13, lineHeight: 1.55 }}><AnnotatedText text={ins} /></Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {(cockpit.signals?.length || cockpit.objections?.length) ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
          {cockpit.signals?.length > 0 && (
            <Box sx={{ ...panelSx, p: 1.5 }}>
              <Typography sx={{ ...microLabelSx, color: '#3ddc97' }}>{t('heybro_signals')}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {cockpit.signals.map((s, i) => (
                  <Typography key={i} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55 }}><AnnotatedText text={s} /></Typography>
                ))}
              </Box>
            </Box>
          )}
          {cockpit.objections?.length > 0 && (
            <Box sx={{ ...panelSx, p: 1.5 }}>
              <Typography sx={{ ...microLabelSx, color: '#f0c24b' }}>{t('heybro_objections')}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {cockpit.objections.map((s, i) => (
                  <Typography key={i} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55 }}><AnnotatedText text={s} /></Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      {(rec.who || rec.channel || rec.occasion || rec.goal) && (
        <Box sx={{ ...panelSx, p: { xs: 1.75, md: 2.25 }, mb: 2,
          borderColor: hexA(ACCENT, 0.45), background: `linear-gradient(135deg, ${hexA(ACCENT, 0.1)}, transparent 70%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FlagOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'primary.main' }}>
              {t('heybro_final_recommendation')}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
            {[['who', t('heybro_rec_who')], ['channel', t('heybro_rec_channel')],
              ['occasion', t('heybro_rec_occasion')], ['goal', t('heybro_rec_goal')]]
              .map(([key, label]) => rec[key] && (
                <Box key={key}>
                  <Typography sx={microLabelSx}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.55 }}><AnnotatedText text={rec[key]} /></Typography>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* Chapter cards — the reader's map into the dossier */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
        {chapters.filter((c) => c.id !== 'overview').map((c) => {
          const Icon = c.icon;
          const count = c.id === 'sources' ? sources.length
            : c.sectionIds.filter((id) => sectionById[id]).length;
          return (
            <ButtonBase key={c.id} onClick={() => goTo(c.id)}
              sx={{ ...panelSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
                justifyContent: 'flex-start', textAlign: 'left', transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': { borderColor: hexA(ACCENT, 0.5), bgcolor: hexA(ACCENT, 0.06) } }}>
              <Icon sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{t(c.labelKey)}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {c.id === 'sources' ? `${count} ${t('heybro_sources_count')}` : `${count} ${t('heybro_sections_count')}`}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 'auto' }} />
            </ButtonBase>
          );
        })}
      </Box>
    </>
  );

  // ── Sources chapter ──
  const renderSources = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {sources.map((s, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px',
          '&:hover': { bgcolor: 'action.hover' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component={s.url ? 'a' : 'span'} href={s.url || undefined}
              target={s.url ? '_blank' : undefined} rel="noreferrer"
              sx={{ fontSize: 12.5, fontWeight: 600, color: s.url ? 'primary.main' : 'text.primary',
                textDecoration: 'none', '&:hover': { textDecoration: s.url ? 'underline' : 'none' } }}>
              {s.title || s.url || '—'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {[s.publisher, s.published_date && formatDate(s.published_date, i18n.language)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <VerificationPill status={s.verification_status} />
        </Box>
      ))}
    </Box>
  );

  // ── A themed chapter: its sections as sub-tabs (never one long scroll) ──
  const renderChapter = (ch) => {
    if (ch.id === 'overview') return renderOverview();
    if (ch.id === 'sources') return renderSources();
    const present = ch.sectionIds.map((id) => sectionById[id]).filter(Boolean);
    if (!present.length) return null;
    const activeId = subTab[ch.id] && present.some((s) => s.id === subTab[ch.id])
      ? subTab[ch.id] : present[0].id;
    const active = present.find((s) => s.id === activeId);
    return (
      <>
        {present.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
            {present.map((s) => (
              <Chip key={s.id} size="small" label={s.title}
                onClick={() => setSubTab((cur) => ({ ...cur, [ch.id]: s.id }))}
                sx={{ height: 28, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  color: s.id === activeId ? 'primary.main' : 'text.secondary',
                  bgcolor: s.id === activeId ? hexA(ACCENT, 0.14) : 'action.hover',
                  border: `1px solid ${s.id === activeId ? hexA(ACCENT, 0.4) : 'transparent'}` }} />
            ))}
          </Box>
        )}
        {present.length === 1 && (
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'primary.main', mb: 1.5 }}>
            {active.title}
          </Typography>
        )}
        {renderSectionContent(active, t)}
      </>
    );
  };

  const prevChapter = chapters[chapterIndex - 1];
  const nextChapter = chapters[chapterIndex + 1];

  return (
    <SourceLinksContext.Provider value={findSourceUrl}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* ─── Toolbar: back + meta + actions ─── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary' }} aria-label="back">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {report.company_name}
          {meta.research_date && ` · ${t('heybro_stand')}: ${formatDate(meta.research_date, i18n.language)}`}
          {report.created_by_name && ` · ${t('heybro_created_by')}: ${report.created_by_name}`}
        </Typography>
        {onExport && (
          <InfoTooltip title={t('heybro_export')}>
            <IconButton size="small" onClick={handleExport} disabled={exporting}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
              {exporting
                ? <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                : <FileDownloadOutlinedIcon fontSize="small" />}
            </IconButton>
          </InfoTooltip>
        )}
        <InfoTooltip title={t('heybro_refresh')}>
          <IconButton size="small" onClick={onRefresh} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </InfoTooltip>
        <InfoTooltip title={t('heybro_delete')}>
          <IconButton size="small" onClick={onDelete} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </InfoTooltip>
      </Box>

      {/* The dossier is researched in German; a translation for this UI
          language is being produced in the background. The canonical text
          below stays readable and is swapped out when it lands. */}
      {translating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 1, borderRadius: '10px',
          bgcolor: hexA(ACCENT, 0.08), border: `1px solid ${hexA(ACCENT, 0.3)}` }}>
          <CircularProgress size={14} sx={{ color: 'primary.main' }} />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('heybro_translating')}</Typography>
        </Box>
      )}

      {/* Failed-refresh note: the previous dossier is still what's shown. */}
      {report.error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 1, borderRadius: '10px',
          bgcolor: 'rgba(240,194,75,0.1)', border: '1px solid rgba(240,194,75,0.35)' }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: '#f0c24b' }} />
          <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>{report.error}</Typography>
        </Box>
      )}

      {/* ─── Mobile chapter nav: wrapping chips (never a horizontal scroll) ─── */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 0.75, flexWrap: 'wrap', pb: 1 }}>
        {chapters.map((c) => (
          <Chip key={c.id} size="small" label={t(c.labelKey)} onClick={() => goTo(c.id)}
            sx={{ height: 26, fontSize: 11.5, fontWeight: 600, flexShrink: 0, cursor: 'pointer',
              color: chapterId === c.id ? 'primary.main' : 'text.secondary',
              bgcolor: chapterId === c.id ? hexA(ACCENT, 0.14) : 'action.hover',
              border: `1px solid ${chapterId === c.id ? hexA(ACCENT, 0.4) : 'transparent'}` }} />
        ))}
      </Box>

      {/* ─── Body: sidebar (desktop) + chapter page ─── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
        {/* Sidebar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 0.25,
          width: 200, flexShrink: 0, pt: 0.5, borderRight: '1px solid', borderColor: 'divider', pr: 1 }}>
          {chapters.map((c) => {
            const Icon = c.icon;
            const active = chapterId === c.id;
            return (
              <ButtonBase key={c.id} onClick={() => goTo(c.id)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'flex-start',
                  px: 1.25, py: 0.9, borderRadius: '10px', textAlign: 'left',
                  color: active ? 'primary.main' : 'text.secondary',
                  bgcolor: active ? hexA(ACCENT, 0.12) : 'transparent',
                  borderLeft: `3px solid ${active ? ACCENT : 'transparent'}`,
                  transition: 'background-color 0.15s, color 0.15s',
                  '&:hover': { bgcolor: active ? hexA(ACCENT, 0.12) : 'action.hover', color: 'primary.main' } }}>
                <Icon sx={{ fontSize: 19, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: active ? 700 : 600, lineHeight: 1.25 }}>
                  {t(c.labelKey)}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {/* Chapter page (its own scroll area; kept short by design) */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box key={chapterId} className="hb-screen hb-screen-fwd"
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pr: 1, pt: 0.5 }}>
            {renderChapter(chapter)}
          </Box>

          {/* Prev / next pager */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            pt: 1, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {prevChapter ? (
              <Button size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />} onClick={() => goTo(prevChapter.id)}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                {t(prevChapter.labelKey)}
              </Button>
            ) : <span />}
            {nextChapter ? (
              <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />} onClick={() => goTo(nextChapter.id)}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: 'primary.main' }}>
                {t(nextChapter.labelKey)}
              </Button>
            ) : <span />}
          </Box>
        </Box>
      </Box>
    </Box>
    </SourceLinksContext.Provider>
  );
}

export default DossierView;
